import {gql} from '@apollo/client';
import * as d3 from 'd3';
import React, {useCallback} from 'react';

import ChemicalStructureSVG from 'chemistry/ChemicalStructureSVG';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ErrorFragment} from 'graphql-client/Error';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {GRAY} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  GetNucleotideImageQuery,
  GetNucleotideImageQueryVariables,
} from './__generated/GetNucleotideImageQuery';
import {DEGENERATE_BASE, NucleotideComponent} from './MonomerConstants';
import {getNucleotideId} from './MonomersUtil';

type Monomer = MonomerQuery['monomers'][number];

// Keep in sync with the colors in nucleotide_image_generation.py::MONOMER_COLORS
// Maps the color of an SVG element in the chemical structure to the monomer type it belongs to.
const HIGHLIGHT_COLOR_TO_NUCLEOTIDE_COMPONENT: Record<string, NucleotideComponent> = {
  'rgb(86, 180, 233)': NucleotideComponent.SUGAR,
  'rgb(0, 158, 115)': NucleotideComponent.BASE,
  'rgb(230, 159, 0)': NucleotideComponent.PHOSPHATE,
};

export const GET_NUCLEOTIDE_IMAGE_QUERY = gql`
  query GetNucleotideImageQuery($nucleotideId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    nucleotideImage(id: $nucleotideId) {
      id
      warnings
      chemicalStructureImage {
        ... on SVGNode {
          base64Svg
        }
        ...Error
      }
    }
  }
  ${ErrorFragment.fragment}
`;

interface NucleotideImageProps {
  sugar: Monomer;
  base: Monomer | typeof DEGENERATE_BASE;
  phosphate: Monomer | null;
  className?: string;
  svgClassName?: string;
  handleMouseAction?: (group: NucleotideComponent | null) => void;
  highlightComponent?: NucleotideComponent | null;
}

/**
 * A React element visualizing the chemical structure of a nucleotide with interactive highlighting on hover.
 * Optionally adds CSS classes to the containing <div> and <svg> image, useful for styling with Aphrodite.
 *
 * @param sugar Sugar component monomer
 * @param base Base component monomer or DEGENERATE_BASE
 * @param phosphate Phosphate component monomer or null for a missing phosphate
 * @param className CSS class to give to the containing <div>
 * @param svgClassName CSS class to give to the chemical structure <svg>
 * @param handleMouseAction Event handler for mousing over or mousing out of a nucleotide component in the SVG
 * @param highlightComponent Nucleotide component to highlight in the SVG, or no highlighting if empty string
 * @returns a React element with a nucleotide chemical structure image with a warning message
 */
function NucleotideImage(props: NucleotideImageProps): JSX.Element {
  const {sugar, base, phosphate, highlightComponent, handleMouseAction} = props;

  const result = useBenchlingQuery<GetNucleotideImageQuery, GetNucleotideImageQueryVariables>(
    GET_NUCLEOTIDE_IMAGE_QUERY,
    {
      variables: {
        nucleotideId: getNucleotideId(sugar, base, phosphate),
      },
    }
  );

  // Do not render image if all components are missing a SMILES string or all are null.
  const shouldRenderImage =
    sugar?.canonicalSmiles ||
    (base !== DEGENERATE_BASE && base?.canonicalSmiles) ||
    phosphate?.canonicalSmiles;

  const ref = useCallback(
    (node: HTMLElement | null) => {
      const svg = d3.select(node).select('svg');

      // D3 selection of (possibly highlighted) SVG elements.
      const candidateHoverables = svg.selectAll('ellipse, path');

      // The D3 binding should be idempotent because it modifies SVG elements within ChemicalStructureSVG.
      // Since ChemicalStructureSVG is memoized, this callback will be called multiple times on the same DOM element.
      const data: Array<NucleotideComponent> = [];
      candidateHoverables.each(function (this: SVGElement) {
        // Maintain idempotency by only binding elements to data if they have not already been bound to data.
        if (!this.dataset.group) {
          // Reverse engineer the nucleotide component based off of the atom or bond's highlight color.
          const group =
            HIGHLIGHT_COLOR_TO_NUCLEOTIDE_COMPONENT[this.style.fill] ||
            HIGHLIGHT_COLOR_TO_NUCLEOTIDE_COMPONENT[this.style.stroke];
          if (group) {
            this.dataset.group = group;
            data.push(group);

            // Expand the stroke width of bonds to make the hover target larger.
            if (this.nodeName === 'path') {
              const oldWidth = parseFloat(this.style.strokeWidth);
              const newWidth = oldWidth === 0 ? 5 : oldWidth * 2;
              this.style.strokeWidth = `${newWidth}px`;
              this.style.strokeLinejoin = 'round';
            }
          }
        }
      });

      // Once we've inferred groupings from colors, we can narrow the selection to only highlighted elements.
      let hoverables = svg.selectAll('ellipse, path[data-group]');

      if (data.length) {
        hoverables = hoverables.data(data);

        // Since hoverable elements are behind text labels, ignore mouse movements on non-hoverable elements.
        svg.selectAll(':not([data-group])').style('pointer-events', 'none');
      }

      hoverables
        // Set up hover listeners for each hoverable element.
        .on('mouseover', (e, selectedGroup: NucleotideComponent) => handleMouseAction?.(selectedGroup))
        .on('mouseout', () => handleMouseAction?.(null))
        // Unhighlight all hoverables.
        .style('stroke', GRAY)
        .style('fill', GRAY)
        .style('stroke-opacity', 0)
        .style('fill-opacity', 0)
        // Highlight all hoverables belonging to highlightComponent.
        .filter((group: NucleotideComponent) => group === highlightComponent)
        .style('stroke-opacity', 1)
        .style('fill-opacity', 1);
    },
    [highlightComponent, handleMouseAction]
  );

  if (!sugar && !base && !phosphate) {
    return <div>Error rendering nucleotide image. Monomers not found.</div>;
  } else if (result.type === QueryResultType.ERROR) {
    return <div>Error rendering nucleotide image.</div>;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const {chemicalStructureImage, warnings} = result.data.nucleotideImage;
    return (
      <div ref={ref}>
        {shouldRenderImage && (
          <ChemicalStructureSVG
            chemicalStructureImage={chemicalStructureImage}
            className={props.className}
            svgClassName={props.svgClassName}
          />
        )}
        <div>{warnings.join(' ')}</div>
      </div>
    );
  }
}

export default React.memo(NucleotideImage);
